import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import { useToast } from '../../../context/ToastContext';
import { getDraftBills, generateBillFromDraft, deleteDraftBill } from '../../../redux/feeRelated/feeHandle';
import { underControl } from '../../../redux/feeRelated/feeSlice';
import { getNepaliMonthOptions, formatToBSDate } from '../../../utils/customDate';


const DraftBills = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { currentUser } = useSelector((state) => state.user);
  const {
    feesList,
    draftLoading,
    draftError,
    draftSuccess,
    generationLoading,
    generationError,
    generationSuccess,
  } = useSelector((state) => state.fee);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);

  const [selectedMonth, setSelectedMonth] = useState("");
  const [searchQuery, setSearchQuery] = useState("");


  useEffect(() => {
    if (!currentUser?._id) return;
    dispatch(getDraftBills(currentUser._id));
    dispatch(underControl());
  }, [currentUser?._id, dispatch]);

  useEffect(() => {
    if (draftError) {
      showToast(draftError, 'error');
      dispatch(underControl());
    }
    if (generationError) {
      showToast(generationError, 'error');
      dispatch(underControl());
    }

    if (draftSuccess) {
      dispatch(getDraftBills(currentUser?._id));
      dispatch(underControl());
    }
    if (generationSuccess) {
      dispatch(getDraftBills(currentUser?._id));
      dispatch(underControl());
      navigate('/Admin/fees');
    }
  }, [
    draftError,
    generationError,
    draftSuccess,
    generationSuccess,
    dispatch,
    currentUser?._id,
    showToast,
    navigate,
  ]);

  const drafts = useMemo(() => {
    if (!feesList || !Array.isArray(feesList)) return [];

    const baseDrafts = feesList
      .filter((d) => d?.billStatus === 'draft')
      .map((d) => ({
        id: d?._id,
        studentId: d?.student?._id || d?.student,
        studentName: d?.student?.name,
        rollNum: d?.student?.rollNum,
        className: d?.student?.sclassName?.sclassName || d?.student?.sclassName,
        totalAmount: d?.totalAmount ?? d?.amount ?? '',
        discount: d?.feeDetails?.[0]?.discount || 0,
        createdAt: d?.createdAt,
        updatedAt: d?.updatedAt,
        status: d?.billStatus || 'draft',
        feeDetailId: d?.feeDetails?.[0]?._id || d?.feeDetails?.[0]?.id || null,
        month: d?.feeDetails?.[0]?.month || '',
      }));

    let filtered = baseDrafts;

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((d) => (d.studentName || '').toLowerCase().includes(query));
    } else {
      if (selectedMonth) {
        const monthLabel = getNepaliMonthOptions().find((m) => m.value === selectedMonth)?.label;
        filtered = filtered.filter((d) => {
          if (!monthLabel) return false;
          return d.month && d.month.includes(monthLabel);
        });
      }
    }

    return filtered;
  }, [feesList, selectedMonth, searchQuery]);


  const confirmDelete = async () => {
    if (!draftToDelete) return;

    try {
      const { studentId } = draftToDelete;
      if (!studentId) {
        showToast('Missing studentId for draft delete', 'error');
        return;
      }

      // Delete the whole draft bill record (backend supports: DELETE /Fee/:studentId)
      await dispatch(deleteDraftBill(studentId));
      showToast('Draft deleted successfully', 'success');
      setDeleteDialogOpen(false);
      dispatch(getDraftBills(currentUser?._id));
      dispatch(underControl());
    } catch (e) {
      showToast('Failed to delete draft', 'error');
    }
  };

  const handleEdit = (item) => {
    navigate('/Admin/addfee', {
      state: {
        draftId: item.id,
        mode: 'draft-edit',
      },
    });
  };

  const handleGenerate = async (item) => {
    await dispatch(generateBillFromDraft(item.id));
    // Ensure UI always redirects to generated fee list after conversion
    navigate('/Admin/fees');
  };


  if (draftLoading && (!feesList || feesList.length === 0)) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          <LinearProgress sx={{ width: 280 }} />
          <Typography color="textSecondary">Loading draft bills...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                Draft Bills
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Edit, generate, or delete draft billing records
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={() => navigate('/Admin/addfee', { state: { mode: 'draft' } })}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
            >
              New Draft
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Paper
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: 2,
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Filter by Month</InputLabel>
                <Select
                  value={selectedMonth}
                  label="Filter by Month"
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <MenuItem value="">All Months</MenuItem>
                  {getNepaliMonthOptions().map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                placeholder="Search student by name..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">🔎</InputAdornment>,
                }}
                sx={{
                  minWidth: 280,
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                }}
              />

              <Box sx={{ flexGrow: 1 }} />
              <Typography variant="caption" color="textSecondary">
                Displaying {drafts.length} records
              </Typography>
            </Stack>
          </Paper>

          {drafts.length === 0 ? (

            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px dashed rgba(0,0,0,0.15)',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                No draft bills found.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create a new draft to start billing.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {drafts.map((item) => (
                <Grid item xs={12} md={6} lg={4} key={item.id}>
                  <Card sx={{ p: 2.5, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{item.studentName || 'Student'}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Roll: {item.rollNum || '-'}
                        </Typography>
                        <Chip sx={{ mt: 1 }} label={item.className || 'Class'} size="small" />
                        {item.month && (
                          <Chip sx={{ mt: 1, ml: 1 }} label={item.month} size="small" color="info" variant="outlined" />
                        )}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'textSecondary' }}>
                          Draft #: {item.id}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 800 }}>
Total: Rs. {((item.totalAmount?.toLocaleString?.() ? item.totalAmount.toLocaleString() : item.totalAmount) || '-')}
                        </Typography>
                        {item.discount > 0 && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main', fontWeight: 700 }}>
                            Discount: Rs. {item.discount.toLocaleString()}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'textSecondary' }}>
                          Created: {item.createdAt ? formatToBSDate(item.createdAt) : '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'textSecondary' }}>
                          Updated: {item.updatedAt ? formatToBSDate(item.updatedAt) : '-'}
                        </Typography>
                      </Box>
                      <Chip label="Draft" color="warning" size="small" sx={{ fontWeight: 800 }} />
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Tooltip title="Edit Draft">
                        <IconButton onClick={() => handleEdit(item)} sx={{ color: '#667eea' }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Generate Bill">
                        <IconButton
                          onClick={() => handleGenerate(item)}
                          disabled={Boolean(generationLoading)}
                          sx={{ color: '#16a34a' }}
                        >
                          <ReceiptLongIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete Draft">
                        <IconButton
                          onClick={() => {
                            setDraftToDelete({
                              id: item.id,
                              studentId: item.studentId,
                            });
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ color: '#ef4444' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Draft Bill?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Are you sure you want to delete this draft bill? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DraftBills;

